import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Button, Breadcrumbs, Link, Grid, Card, CardContent,
    CardActionArea, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Paper, Avatar, Fade, IconButton, useTheme
} from '@mui/material';
import api from '../services/api';
import { Parcours, Bloc, Category } from '../types/course';
import { designTokens } from '../theme/designTokens';
import AddIcon from '@mui/icons-material/Add';
import CategoryIcon from '@mui/icons-material/Category';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LayersIcon from '@mui/icons-material/Layers';
import CategoryModal from '../components/course/modals/CategoryModal';

const BlocDetails = () => {
    const { parcoursId, blocId } = useParams<{ parcoursId: string; blocId: string }>();
    const [parcours, setParcours] = useState<Parcours | null>(null);
    const [bloc, setBloc] = useState<Bloc | null>(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const theme = useTheme();

    // Modal State
    const [categoryModalOpen, setCategoryModalOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState<Category | null>(null);

    useEffect(() => {
        fetchDetails();
    }, [parcoursId, blocId]);

    const fetchDetails = async () => {
        try {
            const res = await api.get('/api/course/parcours');
            const foundParcours = res.data.find((p: Parcours) => p._id === parcoursId);
            if (foundParcours) {
                setParcours(foundParcours);
                const foundBloc = foundParcours.blocs?.find((b: Bloc) => b._id === blocId);
                setBloc(foundBloc || null);
            }
            setLoading(false);
        } catch (err) {
            console.error("Error fetching details", err);
            setLoading(false);
        }
    };

    const handleOpenCategoryModal = (category: Category | null = null) => {
        setEditingCategory(category);
        setCategoryModalOpen(true);
    };

    const handleSaveCategory = async (data: any) => {
        try {
            if (editingCategory) {
                await api.put(`/api/course/category/${editingCategory._id}`, data);
            } else {
                await api.post('/api/course/category', {
                    ...data,
                    blocId: blocId,
                    parentId: null // Top level category in this bloc
                });
            }
            fetchDetails();
        } catch (err) {
            console.error("Error saving category", err);
        }
    };

    if (loading) return <Box sx={{ p: 4 }}>Loading...</Box>;
    if (!parcours || !bloc) return <Box sx={{ p: 4 }}>Bloc not found</Box>;

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)',
            p: { xs: 2, md: 4 }
        }}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
                <Link component={RouterLink} underline="hover" color="inherit" to="/parcours" sx={{ display: 'flex', alignItems: 'center' }}>
                    <ArrowBackIcon sx={{ mr: 0.5, fontSize: 18 }} /> Gestion des Parcours
                </Link>
                <Link component={RouterLink} underline="hover" color="inherit" to={`/parcours/${parcoursId}`}>
                    {parcours.title}
                </Link>
                <Typography color="text.primary" sx={{ fontWeight: 700, fontFamily: 'Quicksand, sans-serif' }}>{bloc.title}</Typography>
            </Breadcrumbs>

            {/* Hero Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 4,
                    p: { xs: 3, md: 5 },
                    mb: 4,
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Decorative circles */}
                <Box sx={{
                    position: 'absolute',
                    top: -40,
                    right: -40,
                    width: 150,
                    height: 150,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.1)',
                }} />
                <Box sx={{
                    position: 'absolute',
                    bottom: -20,
                    right: 60,
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.08)',
                }} />

                <Grid container spacing={4} alignItems="center">
                    <Grid size={{ xs: 12, md: 8 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Avatar sx={{
                                bgcolor: 'rgba(255,255,255,0.2)',
                                width: 56,
                                height: 56,
                                backdropFilter: 'blur(10px)'
                            }}>
                                <CategoryIcon sx={{ fontSize: 28 }} />
                            </Avatar>
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 800, fontFamily: 'Quicksand, sans-serif' }}>
                                    {bloc.title}
                                </Typography>
                                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                    Gestion des catégories de ce bloc de compétences
                                </Typography>
                            </Box>
                        </Box>
                        {bloc.certificationNumber && (
                            <Chip
                                label={`Certification: ${bloc.certificationNumber}`}
                                sx={{
                                    ml: 9,
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                    color: 'white',
                                    fontWeight: 600,
                                    backdropFilter: 'blur(5px)'
                                }}
                            />
                        )}
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenCategoryModal(null)}
                            sx={{
                                bgcolor: 'white',
                                color: '#667eea',
                                fontWeight: 700,
                                px: 4,
                                py: 1.5,
                                borderRadius: 3,
                                textTransform: 'none',
                                boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.95)',
                                    transform: 'translateY(-2px)',
                                },
                                transition: 'all 0.2s ease'
                            }}
                        >
                            Ajouter une Catégorie
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            <Grid container spacing={3}>
                {bloc.categories?.map((category, index) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={category._id}>
                        <Fade in timeout={300 + index * 100}>
                            <Card sx={{
                                height: '100%',
                                borderRadius: 4,
                                transition: 'all 0.3s ease',
                                border: '1px solid rgba(102, 126, 234, 0.15)',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                                '&:hover': {
                                    transform: 'translateY(-5px)',
                                    boxShadow: '0 12px 30px rgba(102, 126, 234, 0.15)',
                                }
                            }}>
                                <CardActionArea
                                    onClick={() => navigate(`/category/${category._id}`)}
                                    sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                                >
                                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
                                            <Avatar sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)', color: '#667eea', width: 40, height: 40 }}>
                                                <CategoryIcon fontSize="small" />
                                            </Avatar>
                                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#2c3e50', fontFamily: 'Quicksand, sans-serif' }}>
                                                {category.title}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ ml: 7 }}>
                                            {category.courses?.length || 0} Cours disponibles
                                        </Typography>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Fade>
                    </Grid>
                ))}
                {(!bloc.categories || bloc.categories.length === 0) && (
                    <Grid size={{ xs: 12 }}>
                        <Paper sx={{
                            textAlign: 'center',
                            py: 8,
                            borderRadius: 4,
                            background: 'rgba(255,255,255,0.5)',
                            border: '2px dashed #e0e0e0',
                        }}>
                            <CategoryIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary">
                                Aucune catégorie dans ce bloc. Commencez par en ajouter une.
                            </Typography>
                        </Paper>
                    </Grid>
                )}
            </Grid>

            <CategoryModal
                open={categoryModalOpen}
                onClose={() => setCategoryModalOpen(false)}
                onSave={handleSaveCategory}
                initialData={editingCategory || undefined}
                isEditing={!!editingCategory}
                availableCompetencies={bloc.competencies || []}
            />
        </Box>
    );
};

export default BlocDetails;

